import { useState, useEffect } from "react";
import {
  Box,
  Button,
  Container,
  Typography,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Table,
  TableBody,
  TableCell,
  TableRow,
  IconButton,
  Grid,
  Paper,
  TableContainer,
  TableHead,
  Tooltip,
} from "@mui/material";
import { useNavigate, useParams } from "react-router-dom";
import {
  DataGrid,
  GridColDef,
  GridToolbarContainer,
  GridToolbarExport,
  jaJP,
} from "@mui/x-data-grid";
import DoorOperationModal from "./DoorOperationModal";
import { SortState } from "../../../common/const";
import { getJwtTokenFromIdToken } from "../auth/AuthFanc";
import BackButton from "../../atoms/Button/BrowserBackButton";
import PageTitle from "../../molecules/PageTitle";
import UpdateButton from "../../atoms/Button/UpdateButton";
import {
  CustomContainer,
  StyledDataGrid,
  customDataGridLocaleTextAll,
  customDataGridLocaleTextLoading,
  customDataGridLocaleTextNoData,
} from "../../../styles/styles";
import SettingsIcon from "@mui/icons-material/Settings";
import LogIcon from "@mui/icons-material/Book";
import IconButtonWithTooltip from "../../atoms/Button/IconButtonWithTooltip";
import NavigateButton from "../../atoms/Button/NavigateButton";
import { CircleOutlined, Close } from "@mui/icons-material";

interface Door {
  autoSdcId: number;
  doorId: string; //TODO remove doorId
  sdcSerialNumber: string;
  isOnline: boolean;
  lastDownloadedAt: Date;
  doorPlace: string;
  isEnabled: boolean;
  memo: string;
  building: {
    autoBuildingId: number;
    buildingId: string;
    buildingManagementId: string;
    buildingName: string;
    buildingAddress: string;
  };
}

const DoorListPage = () => {
  const navigate = useNavigate();
  const { autoBuildingId } = useParams();
  const [doors, setDoors] = useState<Door[]>([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedOperation, setSelectedOperation] = useState("");
  const [isLoading, setIsLoading] = useState(true);

  const operations = [
    "施錠",
    "解錠",
    "開扉",
    "閉扉",
    "自動制御モード",
    "手動制御モード",
  ];
  useEffect(() => {
    // 内部の非同期関数を定義
    const fetchData = async () => {
      // JWTトークンを取得します
      const jwtToken = await getJwtTokenFromIdToken();

      try {
        // call indexSdcsWithAutoBuildingId API
        const response = await fetch(
          process.env.REACT_APP_API_ENDPOINT +
            `/buildings/${autoBuildingId}/sdcs/`,
          {
            headers: {
              "Content-Type": "application/json",
              Authorization: jwtToken,
            },
          }
        );

        const data = await response.json();
        console.log(data);
        setDoors(data.sdcs);
        setIsLoading(false);
      } catch (error) {
        console.error("Error:", error);
        setIsLoading(false);
      }
    };

    // 非同期関数を実行
    fetchData();
  }, [autoBuildingId]);

  const handleModalOpen = () => {
    setModalOpen(true);
  };

  // const handleOperateClick = (doorId: string) => {
  //   console.log("Operate button clicked for door:");
  //   handleModalOpen();
  // };

  const handleModalClose = () => {
    setModalOpen(false);
  };

  const handleDoorSettingsClick = (doorId: string, autoSdcId: number) => {
    navigate(`/buildings/${autoBuildingId}/doors/${autoSdcId}`, {
      state: doorId,
    });
    console.log(`設定 button clicked` + "　選択されたドア" + doorId);
  };

  const handleDoorLogClick = (autoSdcId: number) => {
    navigate(`/buildings/${autoBuildingId}/doors/${autoSdcId}/log`, {
      state: autoSdcId,
    });
    console.log(`ログ button clicked` + "　選択されたドア" + autoSdcId);
  };

  const handleBulkOperationClick = () => {
    console.log(`Selected operation: ${selectedOperation}`);
  };

  const columns: GridColDef[] = [
    // {
    //   field: 'operation', headerName: 'ドア操作', sortable: false, flex: 1,
    //   renderCell: (params: { row: { doorId: any; }; }) => {
    //     const doorId = params.row.doorId;
    //     return (
    //       <Button variant="outlined" onClick={() => handleOperateClick(doorId)}>
    //         操作
    //       </Button>
    //     );
    //   }
    // },
    // {
    //   field: 'isEnabled', headerName: '利用状態', flex: 1,
    //   valueGetter: (params: { row: { isEnabled: any; }; }) => params.row.isEnabled ? "有効" : "無効", renderCell: (params) => (<Tooltip title={params.value}><span>{params.value}</span></Tooltip>)
    // },
    {
      field: "sdcSerialNumber",
      headerName: "SDCSN",
      flex: 1,
      renderCell: (params) => (
        <Tooltip title={params.value}>
          <span>{params.value}</span>
        </Tooltip>
      ),
    },
    {
      field: "isOnline",
      headerName: "接続状態",
      flex: 1,
      renderCell: (params) => (
        <Tooltip title={params.value ? "オンライン" : "オフライン"}>
          {params.value ? (
            <CircleOutlined sx={{ color: "green" }} />
          ) : (
            <Close sx={{ color: "red" }} />
          )}
        </Tooltip>
      ),
    },
    {
      field: "lastDownloadedAt",
      headerName: "SDCが最後に鍵をDLした日時",
      flex: 1.5,
      renderCell: (params) => {
        if (!params.value) {
          return (
            <Tooltip title="記録なし">
              <span>記録なし</span>
            </Tooltip>
          );
        }

        const dateStr = new Date(params.value).toLocaleString("ja-JP", {
          timeZone: "Asia/Tokyo",
          year: "numeric",
          month: "2-digit",
          day: "2-digit",
          hour: "2-digit",
          minute: "2-digit",
          second: "2-digit",
        });
        return (
          <Tooltip title={dateStr}>
            <span>{dateStr}</span>
          </Tooltip>
        );
      },
    },
    {
      field: "doorPlace",
      headerName: "設置場所",
      flex: 1.5,
      renderCell: (params) => (
        <Tooltip title={params.value}>
          <span>{params.value}</span>
        </Tooltip>
      ),
    },
    {
      field: "memo",
      headerName: "メモ",
      flex: 2,
      renderCell: (params) => (
        <Tooltip title={params.value}>
          <span>{params.value}</span>
        </Tooltip>
      ),
    },
    {
      field: "action",
      headerName: "操作",
      sortable: false,
      flex: 1,
      renderCell: (params: { row: { doorId: any; autoSdcId: any } }) => {
        const doorId = params.row.doorId;
        const autoSdcId = params.row.autoSdcId;
        return (
          <>
            <IconButtonWithTooltip
              title="設定・操作"
              onClick={() => handleDoorSettingsClick(doorId, autoSdcId)}
              icon={<SettingsIcon />}
            />
            <IconButtonWithTooltip
              title="ログ"
              onClick={() => handleDoorLogClick(autoSdcId)}
              icon={<LogIcon />}
            />
          </>
        );
      },
    },
  ];

  const rows = (doors || []).map((door, index) => {
    return {
      id: index,
      doorId: door.doorId, //TODO remove doorId
      sdcSerialNumber: door.sdcSerialNumber,
      isOnline: door.isOnline,
      memo: door.memo,
      doorPlace: door.doorPlace,
      isEnabled: door.isEnabled,
      building: door.building,
      autoSdcId: door.autoSdcId,
    };
  });

  return (
    <CustomContainer maxWidth={false}>
      <Box display="flex" justifyContent="space-between" alignItems="center">
        <PageTitle title="管理ドア一覧" />
        <NavigateButton to={"/buildings"} buttonTitle={"管理物件一覧"} />
      </Box>
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="flex-end"
        marginBottom={2}
      >
        <Box maxWidth="lg">
          <TableContainer>
            <Table size="small">
              <TableBody>
                <TableRow>
                  <TableCell component="th" scope="row">
                    物件システムID:
                  </TableCell>
                  <TableCell>{rows[0]?.building.buildingId || ""}</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell component="th" scope="row">
                    取付先コード（物件管理ID）:
                  </TableCell>
                  <TableCell>
                    {rows[0]?.building.buildingManagementId || ""}
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell component="th" scope="row">
                  取付先名（物件名称）:
                  </TableCell>
                  <TableCell>{rows[0]?.building.buildingName || ""}</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell component="th" scope="row">
                    物件住所:
                  </TableCell>
                  <TableCell>
                    {rows[0]?.building.buildingAddress || ""}
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>
        </Box>

        {/* <Box display="flex" alignItems="center">
            <FormControl variant="outlined">
              <InputLabel>操作</InputLabel>
              <Select
                value={selectedOperation}
                onChange={(e) => setSelectedOperation(e.target.value as string)}
                sx={{ marginRight: "8px", minWidth: "80px" }} // minWidthを追加
                size="small">
                {operations.map((operation) => (
                  <MenuItem key={operation} value={operation}>
                    {operation}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <Button
              onClick={handleBulkOperationClick}
              variant="outlined"
              color="primary"
              sx={{ marginRight: "auto" }}
            >一括操作
            </Button>
          </Box> */}
        <Grid item xs={6}>
          <UpdateButton />
        </Grid>
      </Box>
      <div style={{ height: 700, width: "100%" }}>
        <StyledDataGrid
          rows={rows}
          columns={columns}
          // checkboxSelection
          localeText={
            isLoading
              ? customDataGridLocaleTextLoading
              : rows.length === 0
              ? customDataGridLocaleTextNoData
              : customDataGridLocaleTextAll
          }
        />
      </div>
      <DoorOperationModal open={modalOpen} handleClose={handleModalClose} />
    </CustomContainer>
  );
};

export default DoorListPage;
